import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Добавляем токен авторизации к каждому запросу
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Обрабатываем ошибки авторизации
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Не перенаправляем на /login если это сам запрос логина
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      // Удаляем токен и перенаправляем на страницу входа
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (credentials) => api.post('/auth/login', credentials),
  getMe: () => api.get('/auth/me'),
  createUser: (data) => api.post('/auth/users', data),
  getUsers: () => api.get('/auth/users'),
  getUser: (id) => api.get(`/auth/users/${id}`),
  updateUser: (id, data) => api.put(`/auth/users/${id}`, data),
  deleteUser: (id) => api.delete(`/auth/users/${id}`),
};

// Products API
export const productsApi = {
  getAll: (params) => api.get('/products', { params }),
  getAllIds: (params) => api.get('/products/ids/all', { params }),
  getBrandStats: (params) => api.get('/products/brand-stats', { params }),
  getById: (id) => api.get(`/products/${id}`),
  getBySku: (sku) => api.get(`/products/sku/${sku}`),
  create: (data) => api.post('/products', data),
  createBulk: (data) => api.post('/products/bulk', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  updateManual: (id, data) => api.patch(`/products/${id}/manual`, data),
  updateStock: (data) => api.patch('/products/stock/update', data),
  updatePrice: (data) => api.patch('/products/price', data),
  delete: (id) => api.delete(`/products/${id}`),
  bulkDelete: (productIds) => api.post('/products/bulk-delete', { product_ids: productIds }),
  bulkDeactivate: (productIds) => api.post('/products/bulk-deactivate', { product_ids: productIds }),
  bulkZeroPrices: (productIds) => api.post('/products/bulk-zero-prices', { product_ids: productIds }),
  bulkZeroStocks: (productIds) => api.post('/products/bulk-zero-stocks', { product_ids: productIds }),
  sendToWebsite: (productIds) => api.post('/products/send-to-website', { product_ids: productIds }),
};

// Tasks API
export const tasksApi = {
  getAll: (params) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  cancel: (id) => api.post(`/tasks/${id}/cancel`),
  delete: (id) => api.delete(`/tasks/${id}`),
  getStats: () => api.get('/tasks/stats/summary'),
  sendRemains: () => api.post('/tasks/send-remains'),
  sendRemainsBySupplier: (supplierId) => api.post(`/tasks/send-remains/${supplierId}`),
  sendPricesBySupplier: (supplierId) => api.post(`/tasks/send-prices/${supplierId}`),
  sendRemainsSelectedProducts: (productIds) => api.post('/tasks/send-remains-selected', { product_ids: productIds }),
  sendPrices: () => api.post('/tasks/send-prices'),
  sendPricesSelectedProducts: (productIds) => api.post('/tasks/send-prices-selected', { product_ids: productIds }),
  runSchedule: (scheduleId) => api.post(`/tasks/run-schedule/${scheduleId}`),
  downloadJson: (taskId) => api.get(`/tasks/${taskId}/download-json`, { responseType: 'blob' }),
};

// Files API
export const filesApi = {
  uploadXlsSelf: (formData) => api.post('/files/upload-xls-self', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Brands API
export const brandsApi = {
  getAll: (params) => api.get('/brands', { params }),
  getById: (id) => api.get(`/brands/${id}`),
  create: (data) => api.post('/brands', data),
  update: (id, data) => api.put(`/brands/${id}`, data),
  delete: (id) => api.delete(`/brands/${id}`),
};

// Collections API
export const collectionsApi = {
  getAll: (params) => api.get('/collections', { params }),
  getById: (id) => api.get(`/collections/${id}`),
  create: (data) => api.post('/collections', data),
  update: (id, data) => api.put(`/collections/${id}`, data),
  delete: (id) => api.delete(`/collections/${id}`),
};

// Factories API
export const factoriesApi = {
  getAll: () => api.get('/factories'),
  getById: (id) => api.get(`/factories/${id}`),
  create: (data) => api.post('/factories', data),
  update: (id, data) => api.put(`/factories/${id}`, data),
  delete: (id) => api.delete(`/factories/${id}`),
};

// Factory Stocks API
export const factoryStocksApi = {
  getByProduct: (productId) => api.get(`/factory-stocks/${productId}`),
  update: (productId, data) => api.put(`/factory-stocks/${productId}`, data),
};

// Suppliers API
export const suppliersApi = {
  getAll: (params) => api.get('/suppliers', { params }),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  autoAssign: (id) => api.post(`/suppliers/${id}/auto-assign`),
  getConfig: (id) => api.get(`/suppliers/${id}/config`),
  getConfigPublic: (id) => api.get(`/suppliers/${id}/config-public`),
  saveConfig: (id, data) => api.post(`/suppliers/${id}/config`, data),
  downloadAndProcess: (id) => api.post(`/suppliers/${id}/download-and-process`),
  clearStocks: (id) => api.post(`/suppliers/${id}/clear-stocks`),
  clearPrices: (id) => api.post(`/suppliers/${id}/clear-prices`),
  downloadSourceFile: (id) => api.get(`/suppliers/${id}/source-file`, { responseType: 'blob' }),
  testCloudUrl: (data) => api.post('/suppliers/test-cloud-url', data),
};

// Supplier SKU Mapping API
export const skuMappingApi = {
  getAll: (supplierId) => api.get(`/supplier-sku-mapping/${supplierId}`),
  uploadXls: (supplierId, formData) => api.post(`/supplier-sku-mapping/${supplierId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteRow: (supplierId, mappingId) => api.delete(`/supplier-sku-mapping/${supplierId}/${mappingId}`),
  clearAll: (supplierId) => api.delete(`/supplier-sku-mapping/${supplierId}`),
};

// Schedules API
export const schedulesApi = {
  getAll: () => api.get('/schedules'),
  getById: (id) => api.get(`/schedules/${id}`),
  create: (data) => api.post('/schedules', data),
  update: (id, data) => api.put(`/schedules/${id}`, data),
  delete: (id) => api.delete(`/schedules/${id}`),
  toggle: (id) => api.post(`/schedules/${id}/toggle`),
};

// Users API
export const usersApi = {
  getAll: () => api.get('/auth/users'),
  getBasic: () => api.get('/auth/users/list-basic'),
  getById: (id) => api.get(`/auth/users/${id}`),
};

export const fetchUsers = async () => {
  const response = await usersApi.getBasic();
  return response.data;
};

// Helper functions for backward compatibility
export const getBrands = async (params) => {
  const response = await brandsApi.getAll(params);
  return response.data;
};

export const updateBrand = async (id, data) => {
  const response = await brandsApi.update(id, data);
  return response.data;
};

export const fetchSuppliers = async () => {
  const response = await suppliersApi.getAll();
  return response.data;
};

export const fetchSchedules = async () => {
  const response = await schedulesApi.getAll();
  return response.data;
};

export const createSchedule = async (data) => {
  const response = await schedulesApi.create(data);
  return response.data;
};

export const updateSchedule = async (id, data) => {
  const response = await schedulesApi.update(id, data);
  return response.data;
};

export const deleteSchedule = async (id) => {
  const response = await schedulesApi.delete(id);
  return response.data;
};

export const toggleSchedule = async (id) => {
  const response = await schedulesApi.toggle(id);
  return response.data;
};

// Exchange Rates API
export const exchangeRatesApi = {
  getAll: () => api.get('/exchange-rates'),
  update: (currency, rate, isManual = true) =>
    api.put(`/exchange-rates/${currency}`, { rate, is_manual: isManual }),
  resetManual: (currency) => api.post(`/exchange-rates/${currency}/reset-manual`),
  refresh: () => api.post('/exchange-rates/refresh'),
};

export { api };
export default api;
